import React, { useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useQuery } from '@apollo/client/react';
import {
  GetUnitsDocument,
  GetUnitsQuery,
  GetUnitsQueryVariables,
  GetContractsDocument,
  GetContractsQuery,
  GetContractsQueryVariables,
} from '../generated/graphql';
import { Grid } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

interface NewTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    description: string;
    startDate?: string;
    dueDate?: string;
    unitId?: string;
    contractId?: string;
  }) => void;
  loading: boolean;
  unitId?: string;
  contractId?: string;
}

interface TaskFormValues {
  title: string;
  description: string;
  startDate: string;
  dueDate: string;
  entityType: 'unit' | 'contract' | '';
  unitId: string;
  contractId: string;
}

export function NewTaskDialog({
  open,
  onClose,
  onSubmit,
  loading,
  unitId: initialUnitId,
  contractId: initialContractId,
}: NewTaskDialogProps) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<TaskFormValues>({
    defaultValues: {
      title: '',
      description: '',
      startDate: '',
      dueDate: '',
      entityType: initialUnitId ? 'unit' : initialContractId ? 'contract' : '',
      unitId: initialUnitId || '',
      contractId: initialContractId || '',
    },
  });

  const { data: unitsData } = useQuery<GetUnitsQuery, GetUnitsQueryVariables>(GetUnitsDocument, {
    variables: { includeArchived: false },
  });

  const { data: contractsData } = useQuery<GetContractsQuery, GetContractsQueryVariables>(GetContractsDocument);

  useEffect(() => {
    if (open) {
      reset({
        title: '',
        description: '',
        startDate: '',
        dueDate: '',
        entityType: initialUnitId ? 'unit' : initialContractId ? 'contract' : '',
        unitId: initialUnitId || '',
        contractId: initialContractId || '',
      });
    }
  }, [open, reset, initialUnitId, initialContractId]);

  const entityType = watch('entityType');

  const onFormSubmit = (values: TaskFormValues) => {
    const data: Parameters<typeof onSubmit>[0] = {
      title: values.title.trim(),
      description: values.description.trim(),
    };

    if (values.startDate) {
      data.startDate = new Date(values.startDate).toISOString();
    }
    if (values.dueDate) {
      data.dueDate = new Date(values.dueDate).toISOString();
    }
    if (entityType === 'unit' && values.unitId) {
      data.unitId = values.unitId;
    } else if (entityType === 'contract' && values.contractId) {
      data.contractId = values.contractId;
    }

    onSubmit(data);
  };

  // Filter units: status not archived
  const availableUnits = unitsData?.getUnits.filter((p) => p.status !== 'archived') || [];

  // Filter contracts: not archived (no terminationDate) and not deleted
  const availableContracts = contractsData?.getContracts.filter((c) => !c.terminationDate && !c.deletedAt) || [];

  const startDate = watch('startDate');
  const dueDate = watch('dueDate');
  const dateRangeError = startDate && dueDate && new Date(startDate) > new Date(dueDate);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogTitle>Pridať novú úlohu</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                {...register('title', { required: 'Názov je povinný' })}
                autoFocus
                error={!!errors.title}
                helperText={errors.title?.message}
                label="Názov"
                fullWidth
                variant="standard"
                margin="normal"
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                {...register('description')}
                label="Popis"
                fullWidth
                variant="standard"
                margin="normal"
                disabled={loading}
                multiline
                minRows={2}
                maxRows={4}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                {...register('startDate')}
                label="Dátum začatia"
                type="date"
                fullWidth
                variant="standard"
                margin="normal"
                disabled={loading}
                error={!!dateRangeError}
                helperText={dateRangeError ? 'Dátum začatia musí byť menší alebo rovný termínu splnenia' : ''}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                {...register('dueDate')}
                label="Termín splnenia"
                type="date"
                fullWidth
                variant="standard"
                margin="normal"
                disabled={loading}
                error={!!dateRangeError}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Grid>

            {!initialUnitId && !initialContractId && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth margin="normal" variant="standard">
                    <InputLabel>Typ entity</InputLabel>
                    <Controller
                      name="entityType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            // Reset dependent selects when entity type changes
                          }}
                          label="Typ entity"
                          disabled={loading}
                        >
                          <MenuItem value="">Žiadna</MenuItem>
                          <MenuItem value="unit">Nehnuteľnosť</MenuItem>
                          <MenuItem value="contract">Prenájom</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                {entityType === 'unit' && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth margin="normal" variant="standard">
                      <InputLabel>Nehnuteľnosť</InputLabel>
                      <Controller
                        name="unitId"
                        control={control}
                        render={({ field }) => (
                          <Select {...field} label="Nehnuteľnosť" disabled={loading}>
                            <MenuItem value="">Žiadna</MenuItem>
                            {availableUnits.map((p) => (
                              <MenuItem key={p.id} value={p.id}>
                                {p.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}

                {entityType === 'contract' && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth margin="normal" variant="standard">
                      <InputLabel>Prenájom</InputLabel>
                      <Controller
                        name="contractId"
                        control={control}
                        render={({ field }) => (
                          <Select {...field} label="Prenájom" disabled={loading}>
                            <MenuItem value="">Žiadny</MenuItem>
                            {availableContracts.map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.title}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button type="submit" variant="contained" disabled={loading || !!dateRangeError}>
            Pridať
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
